/**
 * Enhanced error logging utility for tracking React child errors and idle timeout issues
 */
export class ErrorLogger {
  private static instance: ErrorLogger;
  private errorCounts = new Map<string, number>();
  private lastErrorTime = new Map<string, number>();
  
  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * Log React child errors with context
   */
  logReactChildError(componentName: string, error: Error, context?: any): void {
    const errorKey = `react-child-${componentName}`;
    const count = this.errorCounts.get(errorKey) || 0;
    const now = Date.now();
    
    this.errorCounts.set(errorKey, count + 1);
    this.lastErrorTime.set(errorKey, now);

    console.error(`[ErrorLogger] React Child Error in ${componentName}:`, {
      error: error.message,
      stack: error.stack,
      count: count + 1,
      context,
      timestamp: new Date(now).toISOString()
    });

    // Check for patterns indicating idle timeout issues
    if (error.message?.includes('Objects are not valid as a React child')) {
      this.logIdleTimeoutPattern(componentName, error);
    }
  }

  /**
   * Log session recovery events
   */
  logSessionEvent(event: string, details?: any): void {
    console.log(`[SessionRecovery] ${event}:`, {
      details,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      online: navigator.onLine
    });
  }

  /**
   * Log dynamic import failures
   */
  logImportFailure(modulePath: string, error: Error, retryCount?: number): void {
    const errorKey = `import-${modulePath}`;
    const count = this.errorCounts.get(errorKey) || 0;
    
    this.errorCounts.set(errorKey, count + 1);

    console.error(`[ErrorLogger] Import Failure:`, {
      modulePath,
      error: error.message,
      retryCount,
      totalFailures: count + 1,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Check for idle timeout error patterns
   */
  private logIdleTimeoutPattern(componentName: string, error: Error): void {
    const idleIndicators = [
      'Failed to fetch',
      'Network request failed',
      'Load failed',
      'Objects are not valid as a React child'
    ];

    const hasIdleIndicator = idleIndicators.some(indicator => 
      error.message?.includes(indicator)
    );

    if (hasIdleIndicator) {
      console.warn(`[ErrorLogger] Potential idle timeout error detected in ${componentName}:`, {
        error: error.message,
        pageHidden: document.hidden,
        lastFocusChange: this.getLastFocusChangeTime(),
        networkStatus: navigator.onLine ? 'online' : 'offline'
      });
    }
  }

  /**
   * Get the last time the page gained/lost focus
   */
  private getLastFocusChangeTime(): string | null {
    try {
      return sessionStorage.getItem('lastFocusChange');
    } catch {
      return null;
    }
  }

  /**
   * Get error statistics
   */
  getErrorStats(): Record<string, { count: number; lastOccurred: string }> {
    const stats: Record<string, { count: number; lastOccurred: string }> = {};
    
    this.errorCounts.forEach((count, key) => {
      const lastTime = this.lastErrorTime.get(key);
      stats[key] = {
        count,
        lastOccurred: lastTime ? new Date(lastTime).toISOString() : 'unknown'
      };
    });

    return stats;
  }

  /**
   * Initialize error tracking
   */
  initialize(): void {
    // Track page focus changes
    document.addEventListener('visibilitychange', () => {
      try {
        sessionStorage.setItem('lastFocusChange', new Date().toISOString());
      } catch {
        // Ignore storage errors
      }
    });

    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      console.error('[ErrorLogger] Unhandled Promise Rejection:', {
        reason: event.reason,
        timestamp: new Date().toISOString()
      });
    });

    // Override console.error to capture additional context
    const originalError = console.error;
    console.error = (...args: any[]) => {
      // Check if this is a React child error
      const message = args.find(arg => 
        typeof arg === 'string' && 
        arg.includes('Objects are not valid as a React child')
      );
      
      if (message) {
        this.logReactChildError('Unknown', new Error(message), args);
      }
      
      originalError.apply(console, args);
    };
  }
}

export const errorLogger = ErrorLogger.getInstance();